import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { RootState } from "../../../../../store";
import {
  setFileName,
  setUploadSuccess,
} from "../../../../../store/steps/stepFiveSlice";
import SpinnerButton from "../../../../../common/SpinnerButton";
import { SIZES } from "../../../../../utils/enums";
import { STATIC_STRINGS } from "../../../../../utils/Constants";
import { fileUploadAPI } from "../../../services";

const FileUploadCard = () => {
  const dispatch = useDispatch();
  const { uploadSuccess, fileName } = useSelector(
    (state: RootState) => state.stepFive
  );
  const [file, setFile] = useState<File | undefined>();
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileUpload = async (file: File) => {
    setUploading(true);
    try {
      const uploadedFileName = (await fileUploadAPI(file)) as string;
      dispatch(setFileName(uploadedFileName));
      dispatch(setUploadSuccess(true));
      setUploading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement & {
      files: FileList;
    };
    setFile(target.files[0]);
    dispatch(setFileName(target.files[0].name));
    dispatch(setUploadSuccess(false));
  };

  const handleUploadClick = () => {
    if (!file) {
      fileInputRef.current?.click();
    } else {
      fileUpload(file);
    }
  };

  const handleDownloadFileFormat = () => {
    const rows = STATIC_STRINGS.FILE_FORMAT_ROWS;

    let csvContent = STATIC_STRINGS.CSV_CONTENT;

    rows.forEach((rowArray) => {
      let row = rowArray.join(",");
      csvContent += row + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", STATIC_STRINGS.DOWNLOAD_FILE_NAME);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-1/2 mr-2 card h-full p-4 flex flex-col justify-between items-start">
      <div className="font-semibold">{STATIC_STRINGS.UPLOAD_HEADING}</div>
      <div className="text-gray-600 font-medium">
        {STATIC_STRINGS.UPLOAD_SUBHEADING}
      </div>
      <div className="flex flex-col xl:flex-row justify-between items-center w-full">
        <input
          type="file"
          accept=".csv, .xlsx"
          onChange={handleFileChange}
          className="hidden"
          ref={fileInputRef}
        />
        <button
          className="btn btn-outline py-1 px-2 flex items-center m-1 xl:m-0"
          onClick={handleDownloadFileFormat}
        >
          <DownloadIcon className="mr-1" /> {STATIC_STRINGS.DOWNLOAD_FORMAT}
        </button>
        {fileName &&
          (uploadSuccess ? (
            <div className="py-1 px-2 m-1 border-2 border-neutral-dark rounded-lg">
              {fileName} {STATIC_STRINGS.UPLOADED_SUCCESSFULLY}
            </div>
          ) : (
            <div className="py-1 px-2 m-1 border-2 border-neutral-dark rounded-lg">
              {STATIC_STRINGS.SELECTED}: {fileName}
            </div>
          ))}
        <SpinnerButton
          className={`btn btn-outline py-1 px-2 flex items-center justify-center w-36 ${
            file && "cursor-pointer"
          } m-1 xl:m-0`}
          loading={uploading}
          size={SIZES.sm}
          variant="outline"
          onClickHandler={handleUploadClick}
        >
          <div className={`flex items-center ${file ? "cursor-pointer" : ""}`}>
            <UploadFileIcon className="mr-1" />
            {file ? STATIC_STRINGS.UPLOAD_FILE : STATIC_STRINGS.SELECT_FILE}
          </div>
        </SpinnerButton>
      </div>
    </div>
  );
};

export default FileUploadCard;
